import axios, { AxiosError } from "axios"; // tslint:disable-line match-default-export-name

import { gGateway } from "@utils/gateway";
import { EntityStore } from "@use/entity-store";

import { gatewayConfig } from "@courses-utils/gateway-auth/src/ts/config";
import { AuthenticatedEkeyUserModel, AuthenticatedUnicUserModel } from "@courses-utils/gateway-auth/src/ts/models";

/**
 * Contains a set of cached requests to fetch authorization status for different
 * users and different products, used to avoid running the same authorization
 * over and over again.
 */
const cache = new EntityStore<Promise<boolean>>({ ttl: 1800 });

/**
 * Specification of response format for the ValidateSubscription endpoint.
 */
type ValidateSubscriptionResponse = {
    Products: {
        ProductId: string;
        ProviderName: string;
        IsAuthorized: boolean;
    }[];
};

/**
 * Helper that'll determine if the given user has been authorized to access the
 * product of the given id.
 */
export async function authorizeUser(
    user: AuthenticatedEkeyUserModel | AuthenticatedUnicUserModel | undefined,
    productIds: string[],
): Promise<boolean> {
    // Bail out if no user was given (in that case he cannot possibly be
    // authenticated)
    if (!user) {
        return false;
    }

    // Generate cache id, so we don't run the same request multiple times
    const cacheId = `${user.id}-${productIds.join(",")}`;

    // If we don't have a cached version of the authorization status yet, then
    // begin fetching it now
    if (!cache.has(cacheId)) {
        cache.set(cacheId, new Promise<boolean>(async (resolve, reject) => {
            try {
                // Prepare an array of product ids, which we're going to check
                // if the user has access to (we should support both isbn13 and
                // isbn10, if an isbn was given)
                const accessKeys: Set<string> = new Set();

                for (const productId of productIds) {
                    if (productId.length === 10) {
                        // If an isbn-10 was given, then push both isbn-13 (by
                        // prepending 978 to the isbn-10) and the raw isbn-10 to
                        // list of product ids
                        accessKeys.add(`978${productId}`);
                        accessKeys.add(productId);
                    } else if (productId.length === 13 && productId.substr(0, 3) === "978") {
                        // If an isbn-13 was given, then push both the raw
                        // isbn-13 and isbn-10 (by removing the 978 prefix) to
                        // list of product ids
                        accessKeys.add(productId);
                        accessKeys.add(productId.substr(3));
                    } else {
                        // ... Otherwise if a non-isbn was given, then look up
                        // only this id
                        accessKeys.add(productId);
                    }
                }

                // Now that we've prepared our list of product ids, reach out to
                // the LoginConnector and ask if the user has been authorized
                // access to this product
                const response = await axios.post<ValidateSubscriptionResponse>(
                    gGateway(`${gatewayConfig.loginConnectorUrl}/api/v2/ValidateSubscriptions/ValidateSubscription`),
                    {
                        SubscriptionAuthentToken: user.auth.token,
                        ClientWebShopName: user.auth.profile,
                        ProductIds: [...accessKeys],
                    },
                );

                // Iterate over response, and check to see if the user has been
                // authorized to access any of the combinations of product id's
                // that we extracted above
                for (const product of response.data.Products) {
                    if (product.IsAuthorized) {
                        resolve(true);
                    }
                }

                // If we get here, the user doesn't hold a valid license for the
                // requested product
                resolve(false);
            } catch (err) {
                const typedErr = <unknown>err;

                if (isAxiosError(typedErr) && typedErr.response && typedErr.response.status === 400) {
                    // If an error 400 was thrown, that simply means that the
                    // user wasn't authorized
                    resolve(false);
                } else {
                    // ... Otherwise an api error occurred, reject the external
                    // promise
                    reject(err);
                }
            }
        }));
    }

    return <Promise<boolean>>cache.get(cacheId);
}

/**
 * Typeguard that verifies if the error object is an AxiosError object.
 */
function isAxiosError(err: unknown): err is AxiosError {
    return err instanceof Error && "config" in err;
}
